import React from 'react';
import { ErrorState, Skeleton } from '@/components/primitives';
import { timeAgo } from '@/hooks/useSystemStatus';
import { describeInterval } from './api';

/**
 * Key numbers, backed only by GET /api/breach-summary (whole-dataset counts,
 * not the capped lists). While it loads the band shows skeletons; if it
 * fails the values read "Unavailable", never 0.
 */
export default function BreachSummaryCards({ summary, schedule, onRetry }) {
  const s = summary.data || {};
  const loading = summary.loading && !summary.data;
  const failed = Boolean(summary.error) && !summary.data;
  const interval = describeInterval(schedule?.recheck_interval_seconds);
  const lastChecked = s.last_checked_at;

  const item = (label, value, sub, tone) => (
    <div className="is-stats-item">
      <span className="is-stats-l">{label}</span>
      {loading ? <Skeleton height={26} width={56} radius={4} />
        : <b className="is-stats-v" data-tone={!failed && value > 0 ? tone : undefined}>{failed ? 'Unavailable' : (value ?? 0).toLocaleString('en-US')}</b>}
      <span className="is-stats-s">{sub}</span>
    </div>
  );

  return (
    <section aria-label="Breach summary" className="cr-summary">
      {summary.error && (
        <ErrorState variant="inline" title={summary.data ? 'Summary may be out of date' : "Couldn't load the breach summary"} error={summary.error} onRetry={onRetry} />
      )}
      <div className="is-stats" style={{ '--is-stats-n': 5 }}>
        {item('Waiting for review', s.unacknowledged, 'Breaches not yet acknowledged', 'high')}
        {item('Passwords exposed', s.passwords_exposed, 'Records that include a password', 'critical')}
        {item('Breaches', s.total_breaches, s.total_breaches ? `${s.unique_breached_emails ?? 0} addresses affected` : 'Across every address')}
        {item('Addresses watched', s.total_emails, 'Checked against breach sources')}
        {item('Domains', s.total_domains, s.total_domains ? `${s.verified_domains ?? 0} verified` : 'None added yet')}
      </div>
      {!loading && !failed && (
        <p className="cr-sweep">
          {s.total_emails
            ? lastChecked
              ? <>Last check <time dateTime={lastChecked} title={new Date(lastChecked).toLocaleString()}>{timeAgo(lastChecked)}</time></>
              : 'No address has been checked yet'
            : 'Nothing is monitored yet'}
          {interval && s.total_emails > 0 && <>. Automatic re-checks run {interval}.</>}
        </p>
      )}
    </section>
  );
}
